import { Step } from 'react-joyride'

interface FeatureTourTargetOptions {
    /** Stable element ID within this release. */
    id: string
    /** Unique release ID. Change it only when this introduction should appear again. */
    release: string
    title: string
    description: string
    order?: number
}

const VALID_ID = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/

/** Spread on the real DOM button/control; no provider or central registration is needed. */
export const featureTourProps = ({ id, release, title, description, order = 0 }: FeatureTourTargetOptions) => {
    if (!VALID_ID.test(id) || !VALID_ID.test(release) || !title.trim() || !description.trim() || !Number.isSafeInteger(order) || order < 0) {
        throw new Error('Feature tours require stable IDs, a title, a description, and a non-negative integer order.')
    }
    return {
        'data-feature-tour': id,
        'data-feature-tour-release': release,
        'data-feature-tour-title': title,
        'data-feature-tour-description': description,
        'data-feature-tour-order': order,
    }
}

export interface DetectedFeatureTour {
    id: string
    steps: Step[]
}

export const isFeatureTourTargetVisible = (element: Element): element is HTMLElement =>
    element instanceof HTMLElement &&
    element.getClientRects().length > 0 &&
    !element.closest('[hidden], [aria-hidden="true"]') &&
    !element.matches(':disabled, [aria-disabled="true"]') &&
    getComputedStyle(element).visibility !== 'hidden'

/** Only mounted, visible controls participate, so permission-gated controls are skipped. */
export const discoverFeatureTours = (root: ParentNode = document): DetectedFeatureTour[] => {
    const groups = new Map<string, { id: string; order: number; step: Step }[]>()
    for (const element of root.querySelectorAll('[data-feature-tour]')) {
        if (!isFeatureTourTargetVisible(element)) continue
        const {
            featureTour: id = '',
            featureTourRelease: release = '',
            featureTourTitle: title = '',
            featureTourDescription: description = '',
            featureTourOrder = '0',
        } = element.dataset
        const order = Number(featureTourOrder)
        if (!VALID_ID.test(id) || !VALID_ID.test(release) || !title.trim() || !description.trim() || !Number.isSafeInteger(order) || order < 0) continue
        const steps = groups.get(release) ?? []
        if (steps.some((step) => step.id === id)) continue
        steps.push({ id, order, step: { target: element, title, content: description, disableBeacon: true, placement: 'auto' } })
        groups.set(release, steps)
    }
    return Array.from(groups, ([release, steps]) => ({
        id: `attribute:${release}`,
        steps: steps.sort((a, b) => a.order - b.order).map(({ step }) => step),
    }))
}
